// Generates a customer-facing invoice/charge PDF ("Cobrança") with ONLY the selected items.
// Opens in a new window with print/save-as-PDF button.

import { fmtBRL } from "./constants";

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const todayBR = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

/**
 * Generate a charge/invoice PDF window.
 *
 * @param {Object} opts
 * @param {{ name: string, phone?: string }} opts.client
 * @param {Array<{
 *   kind: "order" | "sale",
 *   orderNumber?: string,
 *   date?: string,          // dd/mm/yyyy (data de saída ou data da venda)
 *   description: string,    // texto principal (modelo + serviços, ou peça)
 *   items?: string[],       // opcional — lista de serviços realizados
 *   value: number,
 * }>} opts.rows
 * @param {{ from?: string, to?: string }} [opts.period]  // dd/mm/yyyy strings, opcional
 * @param {{ storeName?: string, address?: string, phone?: string, cnpj?: string }} [opts.store]
 */
export function printCobrancaPDF({ client, rows, period, store, discountPct }) {
  const subtotal = rows.reduce((s, r) => s + (Number(r.value) || 0), 0);
  const pct = Math.max(0, Math.min(100, Number(discountPct) || 0));
  const discountValue = subtotal * (pct / 100);
  const total = subtotal - discountValue;
  const storeName = (store && store.storeName) || "Phone Lab · Assistência Técnica";
  const storePhone = (store && store.phone) || "";
  const storeAddress = (store && store.address) || "";
  const storeCnpj = (store && store.cnpj) || "";
  const logoUrl = `${window.location.origin}/logo-sm.png`;

  const periodStr =
    period && (period.from || period.to)
      ? `${period.from || "—"} a ${period.to || "—"}`
      : "";

  const rowsHtml = rows
    .map((r, idx) => {
      const itemsList = (r.items && r.items.length)
        ? `<ul class="items">${r.items.map((it) => `<li>${escapeHtml(it)}</li>`).join("")}</ul>`
        : "";
      return `
        <tr>
          <td class="num">${idx + 1}</td>
          <td class="date">${escapeHtml(r.date || "—")}</td>
          <td class="os">
            ${r.orderNumber ? `<span class="badge-os">${escapeHtml(r.orderNumber)}</span>` : (r.kind === "sale" ? `<span class="badge-sale">Peça</span>` : "—")}
            ${r.imei ? `<div class="imei" title="IMEI ou OS">${escapeHtml(r.imei)}</div>` : ""}
          </td>
          <td class="desc">
            <div class="desc-main">${escapeHtml(r.description || "—")}</div>
            ${itemsList}
          </td>
          <td class="val">R$ ${fmtBRL(r.value)}</td>
        </tr>
      `;
    })
    .join("");

  const html = `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<title>Cobrança — ${escapeHtml(client.name)} — ${todayBR()}</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #f5f7fb; color: #0f172a; font-family: -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; }
  .toolbar { position: sticky; top: 0; z-index: 10; background: #0f172a; color: #fff; padding: 10px 16px; display: flex; gap: 10px; align-items: center; }
  .toolbar h1 { margin: 0; font-size: 14px; font-weight: 600; letter-spacing: 0.02em; }
  .toolbar .spacer { flex: 1; }
  .toolbar button { background: #2563eb; color: #fff; border: none; padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; }
  .toolbar button:hover { background: #1d4ed8; }
  .toolbar button.secondary { background: #334155; }
  .toolbar button.secondary:hover { background: #475569; }
  .page { max-width: 780px; margin: 24px auto; background: #fff; box-shadow: 0 2px 20px rgba(15,23,42,0.08); border-radius: 12px; padding: 32px 36px; }
  header.doc-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; padding-bottom: 18px; border-bottom: 2px solid #0f172a; }
  header .brand { display: flex; align-items: flex-start; gap: 14px; }
  header .brand .logo { width: 68px; height: 68px; object-fit: contain; flex-shrink: 0; }
  header .brand-text h1 { margin: 0; font-size: 20px; font-weight: 800; color: #0f172a; letter-spacing: -0.01em; }
  header .brand-text p { margin: 4px 0 0 0; font-size: 11px; color: #64748b; line-height: 1.5; }
  header .doc-meta { text-align: right; }
  header .doc-meta .kind { font-size: 10px; text-transform: uppercase; letter-spacing: 0.15em; color: #64748b; font-weight: 700; }
  header .doc-meta .title { font-size: 22px; font-weight: 800; color: #0f172a; margin-top: 2px; }
  header .doc-meta .emit { font-size: 11px; color: #64748b; margin-top: 4px; }
  .client-box { display: flex; justify-content: space-between; gap: 24px; margin-top: 20px; padding: 14px 16px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; }
  .client-box .label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.12em; color: #64748b; font-weight: 700; margin-bottom: 2px; }
  .client-box .val { font-size: 14px; font-weight: 600; color: #0f172a; }
  .client-box .val small { color: #64748b; font-weight: 400; font-size: 12px; }
  table.rows { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
  table.rows thead th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; color: #fff; background: #1e40af; padding: 9px 10px; font-weight: 700; }
  table.rows thead th:last-child { text-align: right; }
  table.rows tbody td { padding: 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  table.rows tbody td.num { width: 30px; color: #94a3b8; font-weight: 600; }
  table.rows tbody td.date { width: 84px; white-space: nowrap; color: #475569; }
  table.rows tbody td.os { width: 110px; }
  table.rows tbody td.val { width: 100px; text-align: right; font-weight: 700; color: #0f172a; white-space: nowrap; }
  .badge-os, .badge-sale { display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 700; }
  .badge-os { background: #dbeafe; color: #1d4ed8; }
  .badge-sale { background: #f3e8ff; color: #7e22ce; }
  .imei { margin-top: 3px; font-size: 10px; color: #64748b; font-family: ui-monospace, "SF Mono", Menlo, monospace; }
  .desc-main { font-weight: 600; color: #0f172a; }
  ul.items { margin: 4px 0 0 0; padding-left: 18px; font-size: 11px; color: #475569; }
  .totals { margin-top: 18px; padding: 16px 20px; background: linear-gradient(135deg, #1e40af, #1e3a8a); color: #fff; border-radius: 12px; }
  .totals-grid { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
  .totals .l { font-size: 11px; text-transform: uppercase; letter-spacing: 0.15em; font-weight: 700; opacity: 0.85; }
  .totals .v { font-size: 26px; font-weight: 800; letter-spacing: -0.02em; }
  .discount-row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; font-size: 12px; opacity: 0.9; border-bottom: 1px dashed rgba(255,255,255,0.2); margin-bottom: 8px; }
  .discount-row.subtotal { font-weight: 600; }
  .discount-row.discount { color: #fecaca; }
  .footer-note { margin-top: 22px; padding-top: 16px; border-top: 1px dashed #cbd5e1; font-size: 11px; color: #64748b; line-height: 1.6; }
  .footer-note strong { color: #0f172a; }
  .sign { margin-top: 40px; display: flex; justify-content: space-between; gap: 40px; }
  .sign .line { flex: 1; text-align: center; padding-top: 30px; border-top: 1px solid #94a3b8; font-size: 11px; color: #475569; }
  @page { size: A4; margin: 12mm; }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .page { box-shadow: none; margin: 0; border-radius: 0; padding: 0; }
  }
</style>
</head>
<body>
  <div class="toolbar">
    <h1>Cobrança — ${escapeHtml(client.name)}</h1>
    <div class="spacer"></div>
    <button onclick="window.print()">🖨️ Imprimir / Salvar em PDF</button>
    <button class="secondary" onclick="window.close()">Fechar</button>
  </div>
  <div class="page">
    <header class="doc-header">
      <div class="brand">
        <img class="logo" src="${escapeHtml(logoUrl)}" alt="${escapeHtml(storeName)}" onerror="this.style.display='none'" />
        <div class="brand-text">
          <h1>${escapeHtml(storeName)}</h1>
          <p>
            ${storeAddress ? escapeHtml(storeAddress) + "<br/>" : ""}
            ${storePhone ? "Tel: " + escapeHtml(storePhone) : ""}
            ${storePhone && storeCnpj ? " · " : ""}
            ${storeCnpj ? "CNPJ: " + escapeHtml(storeCnpj) : ""}
          </p>
        </div>
      </div>
      <div class="doc-meta">
        <div class="kind">Recibo de Cobrança</div>
        <div class="title">R$ ${fmtBRL(total)}</div>
        ${pct > 0 ? `<div class="emit" style="color:#dc2626;font-weight:600;">(-${pct}% de desconto)</div>` : ""}
        <div class="emit">Emitido em ${todayBR()}</div>
      </div>
    </header>

    <div class="client-box">
      <div>
        <div class="label">Cliente</div>
        <div class="val">
          ${escapeHtml(client.name)}
          ${client.phone ? `<br/><small>${escapeHtml(client.phone)}</small>` : ""}
        </div>
      </div>
      ${periodStr ? `<div style="text-align:right;">
        <div class="label">Período</div>
        <div class="val" style="font-size:13px;">${escapeHtml(periodStr)}</div>
      </div>` : ""}
      <div style="text-align:right;">
        <div class="label">Itens</div>
        <div class="val">${rows.length}</div>
      </div>
    </div>

    <table class="rows">
      <thead>
        <tr>
          <th>#</th>
          <th>Data</th>
          <th>OS</th>
          <th>Descrição</th>
          <th>Valor</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml || `<tr><td colspan="5" style="text-align:center;padding:20px;color:#94a3b8;font-style:italic;">Nenhum item selecionado</td></tr>`}
      </tbody>
    </table>

    <div class="totals">
      ${pct > 0 ? `
        <div class="discount-row subtotal"><span>Subtotal</span><span>R$ ${fmtBRL(subtotal)}</span></div>
        <div class="discount-row discount"><span>Desconto (${pct}%)</span><span>− R$ ${fmtBRL(discountValue)}</span></div>
      ` : ""}
      <div class="totals-grid">
        <div class="l">Total a receber</div>
        <div class="v">R$ ${fmtBRL(total)}</div>
      </div>
    </div>

    <div class="footer-note">
      <strong>Observação:</strong> Documento gerado eletronicamente para fins de cobrança dos serviços/produtos listados acima.
      Este recibo é referente somente aos itens selecionados neste fechamento — demais serviços em aberto permanecem pendentes.
    </div>

    <div class="sign">
      <div class="line">Assinatura do cliente</div>
      <div class="line">${escapeHtml(storeName)}</div>
    </div>
  </div>
</body>
</html>`;

  const w = window.open("", "_blank");
  if (!w) {
    alert("Habilite pop-ups para gerar o PDF.");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
